import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import APIWrapper from '../utils/graphqlwrapper';
import AppContext from '../context';
import logger from '../utils/logger';
import ClientIdentities from './ClientIdentities';
import ClientList from './ClientList';
import DeliverySettings from './DeliverySettings';
import { createClientRows } from '../utils/tableHelpers';
import { getFavoriteClients } from '../utils/favsAndRecentClients';

const VIEW_COMPONENTS = {
  clientidentities: ClientIdentities,
  deliverysettings: DeliverySettings,
  clientlist: ClientList,
};

function Homepage(props) {
  const {
    view = 'clientlist',
    hasTDCAdminPermission,
    hasDataEngineerPermission,
    hasAnalystPermission,
  } = props;
  const navigate = useNavigate();
  const [graphqlNextToken, setGraphqlNextToken] = useState('');
  const [pageState, setPageState] = useState({
    isLoading: true,
    page: 0,
    pageSize: 50,
    data: [],
    allClients: [],
    total: 0,
    favoriteClients: [],
  });
  const hasFetchedData = useRef(false);
  const { setShouldLogOut, authenticated } = useContext(AppContext);

  useEffect(() => {
    // pull initial data and format.
    // query API for all clients, if next token is returned, save it, if nextToken is set, use it
    const fetchClients = async (token = graphqlNextToken) => {
      const clients = await APIWrapper.queryApi(
        { query: 'listClients' },
        setShouldLogOut,
        { limit: 1000, nextToken: token || null },
      );
      setGraphqlNextToken(clients.nextToken);
      const { formedRows, pinnedRows } = createClientRows(clients.data, getFavoriteClients());
      const allRows = formedRows.concat(pinnedRows);
      setPageState((old) => ({
        ...old,
        isLoading: false,
        data: allRows,
        allClients: allRows,
        total: allRows.length,
        favoriteClients: pinnedRows,
      }));
    };
      // if we haven't fetched data yet but we're authenticated & ready
      // then we should make the api call
    if (!hasFetchedData.current && authenticated) {
      // set synchronously (not in the .then()) so a re-render before the request
      // resolves can't pass this guard again and fire a second concurrent fetch
      hasFetchedData.current = true;
      fetchClients()
        .catch((e) => {
          // allow a retry on a later re-render instead of leaving the page stuck
          hasFetchedData.current = false;
          logger.error(e);
        });
    }
  }, [authenticated]);

  const handleViewChange = (event) => {
    const newView = event.target.value;
    const viewPaths = {
      clientidentities: '/client-identities',
      deliverysettings: '/delivery-settings',
    };
    navigate(viewPaths[newView] || '/clientlist');
  };

  const ViewComponent = VIEW_COMPONENTS[view] || ClientList;

  return (
    <ViewComponent
      clients={pageState.allClients}
      isLoading={pageState.isLoading}
      hasTDCAdminPermission={hasTDCAdminPermission}
      hasDataEngineerPermission={hasDataEngineerPermission}
      hasAnalystPermission={hasAnalystPermission}
      view={view}
      handleViewChange={handleViewChange}
    />
  );
}

export default Homepage;

Homepage.propTypes = {
  view: PropTypes.oneOf(['clientlist', 'clientidentities', 'deliverysettings']),
  hasTDCAdminPermission: PropTypes.bool,
  hasDataEngineerPermission: PropTypes.bool,
  hasAnalystPermission: PropTypes.bool,
};
